import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";
import { QueryTrigger, SequenceTriggerAlertResult } from "@app2/clients/types/alert-typedefs";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { Domain } from "@app2/alert/constants";
import { TimeService } from "@app2/shared/services/time.service";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { ImportantFieldData } from "@app2/search/search-shared/search-results-table.component";

@UntilDestroy()
@Component({
    selector: "sequence-alert-result",
    template: `
        <div *ngFor="let resultStep of alertResult.stepResults; index as i; first as isFirst; last as isLast">
            <sequence-alert-result-step [query]="getQuery(i)"
                                        [resultStep]="resultStep"
                                        [stepQuery]="getStepQuery(i, true, true)"
                                        [combinedQuery]="getCombinedQuery(i)"
                                        [combinedEnd]="getCombinedEnd(i)"
                                        [stepNumber]="i + 1"
                                        [isFirst]="isFirst"
                                        [isLast]="isLast"
                                        [showAlertStoryLink]="showAlertStoryLink"
                                        [trigger]="trigger"
                                        [customColumns]="customColumns"
                                        (importantFieldDataChanged)="importantFieldDataChanged.emit($event)"
                                        [attr.test-id]="'sequence-step-' + i"
            ></sequence-alert-result-step>
        </div>
    `,
})
export class SequenceAlertResultComponent {
    @Input() trigger: QueryTrigger; // guaranteed to be a sequence trigger
    @Input() alertResult: SequenceTriggerAlertResult;
    @Input() showAlertStoryLink: boolean = false;
    @Input() customColumns: string[] = [];

    @Output() importantFieldDataChanged = new EventEmitter<ImportantFieldData[]>();

    savedQueries: Record<uuid, SavedQuery> = {};

    constructor(@Inject(PRODUCT_DOMAIN) public productDomain: Domain,
                private timeService: TimeService,
                private savedQueryService: SavedQueryService) {
        this.savedQueryService.getSavedQueries$()
            .pipe(untilDestroyed(this))
            .subscribe(savedQueries => this.savedQueries = savedQueries);
    }

    //this function adds the match results to the queries for individual alerts
    //unless otherwise specified queries should include both input values,
    //which are stored in the match result of the prior step, and output values,
    //which are stored in the match result for the current step
    //the first step will not have input values and the last step will not have output values
    getStepQuery(index: number, outputs: boolean, inputs: boolean) {
        const currentValues = this.alertResult.stepResults[index].matchValues;
        const priorValues = this.alertResult.stepResults[index - 1]
            ? this.alertResult.stepResults[index - 1].matchValues
            : [];

        // the number of steps of the trigger may differ from the number of step results
        let stepQuery = this.getQuery(index);
        currentValues.forEach(currentValue => {
            if (outputs) {
                stepQuery += ` ${ this.getStepKeyValuePair(currentValue.outputKey, currentValue.value) }`;
            }
        });
        priorValues.forEach(priorValue => {
            const priorKeyValuePair = this.getStepKeyValuePair(priorValue.inputKey, priorValue.value);
            if (inputs && !stepQuery.includes(priorKeyValuePair)) {
                stepQuery += ` ${ priorKeyValuePair }`;
            }
        });
        return stepQuery;
    }

    // We need to sanitize value terms by surrounding with double quotes and also make sure to escape value terms that
    // already contain double quotes.
    // We capture the non-backslash character before any match and place it in the replace value (the $1 capture group)
    // so that we don't strip any characters from the query.
    private getStepKeyValuePair(key: string, value: string): string {
        return `${ key }: "${ value.replace(/([^\\])"/g, '$1\\"') }"`;
    }

    getQuery(index: number): string {
        return (this.trigger.seqTriggerSteps[index]
                ? this.savedQueries[this.trigger.seqTriggerSteps[index].savedQueryId]?.query
                : "") ?? "";
    }

    //the combined query is used when clicking on the "matched values" in an alert
    //it should include the query above it with just its output values
    //and the query below it with just its input values
    getCombinedQuery(index: number) {
        let topQuery = this.getStepQuery(index, true, false);
        let bottomQuery = this.alertResult.stepResults[index + 1]
            ? this.getStepQuery(index + 1, false, true)
            : "";
        return bottomQuery && (bottomQuery !== topQuery) ? `(${ topQuery }) OR (${ bottomQuery })` : topQuery;
    }

    getCombinedEnd(index: number): string {
        return this.alertResult.stepResults[index + 1] ? this.alertResult.stepResults[index + 1].triggeredOn : "";
    }
}

